/**
 * G2eGente - Cadastro/Edição de Funcionário
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { employeesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, 
  Loader2,
  Save,
  User,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Calendar,
  Users,
  CreditCard,
  CheckCircle,
  AlertCircle,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

const TIPOS_CONTRATO = [
  { value: 'CLT', label: 'CLT' },
  { value: 'PJ', label: 'PJ' },
  { value: 'Estágio', label: 'Estágio' },
  { value: 'Aprendiz', label: 'Aprendiz' },
  { value: 'Temporário', label: 'Temporário' }
];

const SITUACOES = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'desligado', label: 'Desligado' },
  { value: 'afastado', label: 'Afastado' },
  { value: 'ferias', label: 'Férias' }
];

// Máscara de CPF
const formatCPF = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

// Validação de CPF
const validateCPF = (cpf) => {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1+$/.test(digits)) return false;
  
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let d1 = (sum * 10 % 11) % 10;
  if (d1 !== parseInt(digits[9])) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  let d2 = (sum * 10 % 11) % 10;
  if (d2 !== parseInt(digits[10])) return false;
  
  return true;
};

export default function EmployeeForm() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!employeeId;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  const [form, setForm] = useState({
    nome: '',
    cpf: '',
    email: '',
    telefone: '',
    cargo: '',
    codigo_cargo: '',
    departamento: '',
    data_admissao: '',
    tipo_contrato: 'CLT',
    gestor_id: '',
    situacao: 'ativo',
    acesso_funcionalidades_empresa: false
  });
  
  const [cpfError, setCpfError] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, [employeeId]);

  const fetchInitialData = async () => {
    try {
      const [empRes, deptRes] = await Promise.all([
        employeesAPI.getAll(),
        employeesAPI.getDepartments()
      ]);
      setEmployees(empRes.data.filter(e => e.id !== employeeId));
      setDepartments(deptRes.data.departments || []);
      
      if (isEditing) {
        try {
          const empData = await employeesAPI.getById(employeeId);
          setForm({
            nome: empData.data.nome || '',
            cpf: empData.data.cpf ? formatCPF(empData.data.cpf) : '',
            email: empData.data.email || '',
            telefone: empData.data.telefone || '',
            cargo: empData.data.cargo || '',
            codigo_cargo: empData.data.codigo_cargo || '',
            departamento: empData.data.departamento || '',
            data_admissao: empData.data.data_admissao || '',
            tipo_contrato: empData.data.tipo_contrato || 'CLT',
            gestor_id: empData.data.gestor_id || '',
            situacao: empData.data.situacao || empData.data.status || 'ativo',
            acesso_funcionalidades_empresa: empData.data.acesso_funcionalidades_empresa || false
          });
        } catch (empError) {
          console.error('Erro ao carregar funcionário:', empError);
          if (empError.response?.status === 404) {
            toast.error('Funcionário não encontrado');
            navigate('/company/employees');
            return;
          }
          throw empError;
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.nome || !form.cargo) {
      toast.error('Nome e cargo são obrigatórios');
      return;
    }
    
    // Validar CPF se preenchido
    if (form.cpf) {
      if (!validateCPF(form.cpf)) {
        setCpfError('CPF inválido');
        toast.error('CPF inválido');
        return;
      }
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        cpf: form.cpf ? form.cpf.replace(/\D/g, '') : null,
        gestor_id: form.gestor_id || null
      };
      
      if (isEditing) {
        await employeesAPI.update(employeeId, data);
        toast.success('Funcionário atualizado com sucesso');
      } else {
        await employeesAPI.create(data);
        toast.success('Funcionário cadastrado com sucesso');
      }
      navigate('/company/employees');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao salvar funcionário';
      if (message.includes('CPF')) {
        setCpfError(message);
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleCpfChange = (value) => {
    const formatted = formatCPF(value);
    setForm({ ...form, cpf: formatted });
    setCpfError('');
  };
  
  const handleCpfBlur = () => {
    if (form.cpf && form.cpf.replace(/\D/g, '').length === 11) {
      if (!validateCPF(form.cpf)) {
        setCpfError('CPF inválido');
      }
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="employee-form">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/company/employees')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isEditing ? 'Editar Funcionário' : 'Novo Funcionário'}
            </h1>
            <p className="text-slate-600">
              {isEditing ? 'Atualize os dados do funcionário' : 'Cadastre um novo membro da equipe'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-cyan-600" />
                Dados do Funcionário
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="nome">Nome Completo *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="nome"
                    name="employee_name_field"
                    autoComplete="off"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    placeholder="Nome do funcionário"
                    className="pl-10"
                    required
                    data-testid="employee-nome"
                  />
                </div>
              </div>

              {/* CPF - Campo obrigatório para vinculação com App */}
              <div className="space-y-2">
                <Label htmlFor="cpf" className="flex items-center gap-2">
                  CPF 
                  <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                    Obrigatório para vincular ao App
                  </span>
                </Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="cpf"
                    value={form.cpf}
                    onChange={(e) => handleCpfChange(e.target.value)}
                    onBlur={handleCpfBlur}
                    placeholder="000.000.000-00"
                    className={`pl-10 ${cpfError ? 'border-red-500' : ''}`}
                    maxLength={14}
                    data-testid="employee-cpf"
                  />
                </div>
                {cpfError && (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {cpfError}
                  </p>
                )}
                {form.cpf && validateCPF(form.cpf) && !cpfError && (
                  <p className="text-green-600 text-xs flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    CPF válido
                  </p>
                )}
                <p className="text-xs text-slate-500">
                  O CPF é usado para vincular o funcionário ao aplicativo mobile G2eGente
                </p>
              </div>

              {/* Email e Telefone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="email@empresa.com"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="telefone"
                      value={form.telefone}
                      onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                      placeholder="(11) 99999-9999"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Cargo, Código do Cargo e Departamento */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cargo">Cargo *</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="cargo"
                      name="employee_cargo_field"
                      autoComplete="off"
                      value={form.cargo}
                      onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                      placeholder="Ex: Analista de RH"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="codigo_cargo">Código do Cargo</Label>
                  <Input
                    id="codigo_cargo"
                    name="employee_codigo_cargo_field"
                    autoComplete="off"
                    value={form.codigo_cargo}
                    onChange={(e) => setForm({ ...form, codigo_cargo: e.target.value.toUpperCase() })}
                    placeholder="Ex: TI-001"
                    data-testid="employee-codigo-cargo"
                  />
                  <p className="text-xs text-slate-500">Código da folha de pagamento</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="departamento">Departamento</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="departamento"
                      name="employee_dept_field"
                      autoComplete="off"
                      value={form.departamento}
                      onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                      placeholder="Ex: Recursos Humanos"
                      className="pl-10"
                      list="departments-list"
                    />
                    <datalist id="departments-list">
                      {departments.map(dept => (
                        <option key={dept} value={dept} />
                      ))}
                    </datalist>
                  </div>
                </div>
              </div>

              {/* Data Admissão, Tipo Contrato e Situação */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data_admissao">Data de Admissão</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="data_admissao"
                      type="date"
                      value={form.data_admissao}
                      onChange={(e) => setForm({ ...form, data_admissao: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Contrato</Label>
                  <Select 
                    value={form.tipo_contrato} 
                    onValueChange={(v) => setForm({ ...form, tipo_contrato: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_CONTRATO.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Situação</Label>
                  <Select 
                    value={form.situacao} 
                    onValueChange={(v) => setForm({ ...form, situacao: v })}
                  >
                    <SelectTrigger data-testid="employee-situacao">
                      <UserCheck className="w-4 h-4 mr-2 text-slate-400" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SITUACOES.map(sit => (
                        <SelectItem key={sit.value} value={sit.value}>
                          {sit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Gestor */}
              <div className="space-y-2">
                <Label>Gestor Direto</Label>
                <Select 
                  value={form.gestor_id || 'none'} 
                  onValueChange={(v) => setForm({ ...form, gestor_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <Users className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Selecione o gestor (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem gestor</SelectItem>
                    {employees.filter(e => e.status === 'ativo').map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.nome} - {emp.cargo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Acesso às funcionalidades da empresa */}
              <div className="p-4 bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-800 rounded-lg space-y-3">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="acesso_funcionalidades_empresa"
                    checked={form.acesso_funcionalidades_empresa}
                    onChange={(e) => setForm({ ...form, acesso_funcionalidades_empresa: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-cyan-300 text-cyan-600 focus:ring-cyan-500"
                    data-testid="acesso-funcionalidades-checkbox"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="acesso_funcionalidades_empresa" className="text-cyan-700 dark:text-cyan-300 font-medium cursor-pointer">
                      Acesso às funcionalidades da empresa
                    </Label>
                    <p className="text-xs text-cyan-600 dark:text-cyan-400">
                      Quando ativado, o funcionário poderá acessar automaticamente as funcionalidades da empresa no app 
                      (Treinamentos, Avaliações, DISC, Denúncias) utilizando seu CPF. O funcionário ainda precisará 
                      criar sua conta no app com email pessoal.
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigate('/company/employees')}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit"
                  className="bg-cyan-600 hover:bg-cyan-700"
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {isEditing ? 'Salvar Alterações' : 'Cadastrar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </DashboardLayout>
  );
}
